/**
 * @module app.cd
 */

import { autoinject, transient } from "aurelia-framework";
import { Api } from 'app/shared/lib/api/api';
import { IQueryLoadOptions, IApiQueryResults, IApiActionResults } from 'app/shared/lib/api/api-types';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import * as CD from 'app/cd/models/essette-api';


export interface IClaimSearchFilters {
    general?: string;
    memberId?: string | null;
    detailFilter?: string | null;
}

/**  Provides access to API models related to CD Claims */
@transient()
@autoinject()
export class ClaimService {

    ////****************** Fields and Properties ************************************////


    ////****************** Functions ************************************************////
    constructor(private readonly api: Api, private readonly logger: AppLogger) { }


    ////****************** Queries **************************************************////

    /**
     * Load all `CD.Claim` models
     * @param options query load options
     * @param includes related models to include
     */

    public async search(claimSearchFilters: IClaimSearchFilters, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.Claim>> {
        const query = this.api
            .query('cd/claims/')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(claimSearchFilters.memberId) {
            query.filterIdentifier('member.id', claimSearchFilters.memberId)
        }
        if(claimSearchFilters.detailFilter) {
            // Use an or search to look at various diagnoses, service, and medication data
            query.filterOr(query => query
                .filterExists('services', (f =>
                    f.filterString('code', claimSearchFilters.detailFilter!, 'contains')
                ))
                .filterExists('services', (f =>
                    f.filterString('description', claimSearchFilters.detailFilter!, 'contains')
                ))
                .filterExists('diagnoses', (f =>
                    f.filterString('code', claimSearchFilters.detailFilter!, 'contains')
                ))
                .filterExists('diagnoses', (f =>
                    f.filterString('description', claimSearchFilters.detailFilter!, 'contains')
                )));
        }
        return await this.api.get(query);
    }

    public async loadClaims(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.Claim>> {
        const query = this.api
            .query('cd/claims')
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    public async loadServicesByClaim(claimId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.Service>> {
        const query = this.api
            .query(`cd/claims/${claimId}/services`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadDiagnosesByClaim(claimId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.Diagnosis>> {
        const query = this.api
            .query(`cd/claims/${claimId}/diagnoses`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadNotesByClaim(claimId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.ClaimNote>> {
        const query = this.api
            .query(`cd/claims/${claimId}/notes`)
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    ////****************** Loads ****************************************************////
    /**
     * Gets a specific `CD.Claim` record
     * @param claimId The id of the CD.Claim record to get
     * @param includes related models to include
     */
    public async loadClaim(claimId: string, includes: string[] = []): Promise<IApiQueryResults<CD.Claim>> {
        const query = this.api
            .query(`cd/claims/${claimId}`)
            .include(...includes);
        return await this.api.get(query);
    }


    ////****************** Actions **************************************************////

}
